import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  ExternalLink,
  Lock,
  MessageSquare,
  Radio,
  Sparkles,
  Ticket,
  Zap,
} from "lucide-react";
import { api } from "../api.ts";
import { db } from "../db.ts";
import { Spinner } from "../components/Spinner.tsx";
import { normalizePhone } from "../../../src/phoneUtils.ts";

export const UpgradePage = () => {
  const [searchParams] = useSearchParams();
  const queryPhone = searchParams.get("phone") || "";
  const { user } = db.useAuth();

  const [phone, setPhone] = useState(queryPhone);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: instantUserData } = db.useQuery(
    user?.id
      ? {
        $users: {
          $: { where: { id: user.id } },
        },
      }
      : null,
  );

  const instantUser = instantUserData?.$users?.[0];

  useEffect(() => {
    if (!phone && instantUser?.phone) {
      setPhone(instantUser.phone);
    }
  }, [instantUser?.phone, phone]);

  const handleCheckout = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError(null);

    const clean = phone.trim();
    if (!clean) {
      setError("Please enter your WhatsApp phone number to continue.");
      return;
    }

    const normalized = normalizePhone(clean);
    if (normalized.length < 8 || normalized.length > 16) {
      setError(
        "Please enter a valid phone number with country code (e.g. 0501234567 or +972501234567).",
      );
      return;
    }

    setLoading(true);
    try {
      const res = await api({
        endpoint: "createSubscriptionCheckoutSession",
        payload: { phone: normalized },
      });
      if (res?.checkoutUrl) {
        globalThis.location.href = res.checkoutUrl;
      } else {
        throw new Error("Did not receive checkout URL from server.");
      }
    } catch (err: unknown) {
      console.error("Failed to create checkout session:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to initiate checkout. Please try again or message us on WhatsApp.",
      );
      setLoading(false);
    }
  };

  const perks = [
    {
      icon: MessageSquare,
      title: "Unlimited WhatsApp Conversations",
      desc:
        "Remove the 30-message monthly limit and chat with Agent FOMO anytime without restrictions.",
    },
    {
      icon: Radio,
      title: "Unlimited Event Recommendations",
      desc:
        "Get endless personalized picks, tonight's set times, and curated weekend agendas.",
    },
    {
      icon: Zap,
      title: "Instant Push & Match Alerts",
      desc:
        "Real-time alerts sent directly to WhatsApp as soon as secret events matching your vibe are announced.",
    },
    {
      icon: Ticket,
      title: "Ticket Trades & Secret Listings",
      desc:
        "Priority notifications for sold-out events and peer ticket trades.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#141419] via-[#0c0c10] to-black text-white selection:bg-purple-600 selection:text-white relative overflow-hidden flex flex-col justify-between">
      {/* Ambient background glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[450px] bg-purple-600/15 blur-[150px] pointer-events-none rounded-full"
        aria-hidden="true"
      />

      {/* Top Header */}
      <header className="container mx-auto px-4 py-6 flex items-center justify-between relative z-10">
        <Link
          to="/"
          className="flex items-center gap-3 group transition-transform active:scale-95"
        >
          <img
            src="/logo.jpg"
            alt="Agent FOMO"
            className="h-9 w-9 rounded-xl object-cover shadow-md shadow-purple-500/20 border border-purple-500/20 group-hover:scale-105 transition-transform"
          />
          <span className="text-base font-black tracking-tight text-white group-hover:text-purple-200 transition-colors">
            AGENT{" "}
            <span className="bg-gradient-to-r from-purple-400 via-pink-500 to-rose-400 bg-clip-text text-transparent">
              FOMO
            </span>
          </span>
        </Link>
        <div className="flex items-center gap-3">
          <Link
            to="/events"
            className="text-xs font-semibold text-gray-400 hover:text-white px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition"
          >
            Explore Events
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 sm:py-12 relative z-10 max-w-4xl">
        <div className="text-center max-w-2xl mx-auto mb-10 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-purple-300 text-xs font-bold uppercase tracking-wider mb-5">
            <Sparkles className="w-3.5 h-3.5 text-purple-400" />
            <span>Agent FOMO Premium</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-4 text-white">
            Unlock Unlimited Access
          </h1>
          <p className="text-sm sm:text-lg text-gray-300 leading-relaxed font-light">
            Never hit a message wall again. Enjoy full, unrestricted event
            discovery and real-time alerts on WhatsApp.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
          {/* Perks list (Left / 7 cols) */}
          <div className="md:col-span-7 space-y-4">
            <h2 className="text-xs font-bold uppercase tracking-wider text-purple-400/90 px-1">
              What's Included
            </h2>
            <div className="space-y-3">
              {perks.map((perk, i) => {
                const Icon = perk.icon;
                return (
                  <div
                    key={i}
                    className="p-4 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-purple-500/30 transition flex items-start gap-3.5"
                  >
                    <div className="w-10 h-10 rounded-xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center text-purple-300 shrink-0 mt-0.5">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-white mb-1">
                        {perk.title}
                      </h3>
                      <p className="text-xs text-gray-400 leading-relaxed">
                        {perk.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pricing & Checkout Card (Right / 5 cols) */}
          <div className="md:col-span-5">
            <div className="glass-card p-6 sm:p-7 rounded-3xl border border-purple-500/30 shadow-2xl shadow-purple-950/40 relative overflow-hidden space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-white/10">
                <div>
                  <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">
                    Monthly Plan
                  </span>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-3xl sm:text-4xl font-black text-white">
                      ₪39
                    </span>
                    <span className="text-xs text-gray-400 font-medium">
                      / month
                    </span>
                  </div>
                </div>
                <span className="px-3 py-1 rounded-full text-[11px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                  Cancel anytime
                </span>
              </div>

              {error && (
                <div className="p-3.5 rounded-xl bg-red-500/15 border border-red-500/30 text-red-300 text-xs leading-relaxed">
                  {error}
                </div>
              )}

              <form onSubmit={handleCheckout} className="space-y-4">
                <div>
                  <label
                    htmlFor="whatsapp-phone"
                    className="block text-xs font-semibold text-gray-300 mb-1.5"
                  >
                    Your WhatsApp Phone Number
                  </label>
                  <input
                    id="whatsapp-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="e.g. 0501234567 or +972501234567"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-black/40 border border-white/15 text-white placeholder-gray-500 text-sm font-mono focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition"
                    required
                  />
                  <p className="text-[11px] text-gray-400 mt-1.5 leading-relaxed">
                    Premium will be automatically activated for this phone
                    number upon payment.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-2xl bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 hover:from-purple-500 hover:to-rose-500 text-white font-bold text-sm shadow-lg shadow-purple-600/30 hover:scale-[1.02] active:scale-[0.98] transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading
                    ? (
                      <>
                        <Spinner className="w-4 h-4 text-white" />
                        <span>Connecting to Stripe...</span>
                      </>
                    )
                    : (
                      <>
                        <span>Proceed to Stripe Checkout</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                </button>
              </form>

              <div className="space-y-2 pt-2 border-t border-white/10 text-center">
                <div className="flex items-center justify-center gap-1.5 text-[11px] text-gray-400">
                  <Lock className="w-3.5 h-3.5 text-gray-400" />
                  <span>Secure 256-bit SSL encrypted Stripe payment</span>
                </div>
                <p className="text-[10px] text-gray-500">
                  By subscribing, you agree to our{" "}
                  <Link
                    to="/terms"
                    className="text-gray-400 hover:text-white underline"
                  >
                    Terms
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/privacy"
                    className="text-gray-400 hover:text-white underline"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
            </div>

            {/* Direct support note */}
            <div className="mt-4 p-4 rounded-2xl bg-white/[0.02] border border-white/5 text-center">
              <p className="text-xs text-gray-400 mb-2">
                Have questions or need manual activation?
              </p>
              <a
                href="https://wa.me/972504244206"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                <span>Message Agent FOMO Support on WhatsApp</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-8 border-t border-white/5 text-center text-xs text-gray-500 relative z-10">
        <p>© {new Date().getFullYear()} Agent FOMO. All rights reserved.</p>
      </footer>
    </div>
  );
};
